import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MEETING_PROVIDER_LABELS } from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { dateTime, relative } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { Badge, EmptyState, ErrorState, PageHead, SearchBox } from '../../components/ui';
import { OwnerChip } from '../../components/People';
import { useToast } from '../../components/Toast';
import { BookingLinkButton } from '../../components/BookingLink';
import { ActivityDetail } from './ActivityDetail';
import { NewActivityModal } from './NewActivityModal';
import { RecordingPlayerModal } from '../../components/MeetingRecording';
import { GrainSwitch, type Grain } from '../../components/PeriodNav';
import {
  LayoutColumn,
  ProfileLayoutBar,
  ProfileLayoutButton,
  ProfileLayoutProvider,
  Section,
  useProfileLayout,
  type SectionDef,
} from '../../components/ProfileLayout';

/**
 * شاشة الاجتماعات — تُقرأ قبل أن تُقلَّب.
 *
 * ثلاث طبقات: حالٌ يُقرأ بنظرة (كم اليوم، وكم فات موعده)، ثم تحليلٌ يجيب
 * عن «متى نجتمع» و«كيف» — أسبوعٌ قادم بأعمدته وتوزيعُ الوسائل — ثم
 * القائمة مجمَّعةً بأيامها لا سيلًا واحدًا من خمسين سطرًا.
 *
 * والعدُّ من الصفحة المجلوبة لا باستعلامٍ ثانٍ: الخمسون القادمة هي ما
 * تعرضه الشاشة أصلًا، فالأرقام تصف ما تحتها لا شيئًا غائبًا عنها.
 */

interface MeetingRow {
  id: string;
  subject: string;
  body: string | null;
  dueAt: string | null;
  completedAt: string | null;
  durationMin: number | null;
  assigneeId: string | null;
  location: string | null;
  meetingProvider: string | null;
  meetingUrl: string | null;
  mapsUrl?: string | null;
  recordingUrl?: string | null;
  account: { id: string; name: string } | null;
  lead: { id: string; name: string; mapsUrl?: string | null } | null;
  contact: { id: string; name: string } | null;
}

/**
 * ألوان الوسائل — أربعُ هويّاتٍ ثابتة الترتيب لا تُدوَّر.
 *
 * مُدقَّقةٌ بمِسطرة عمى الألوان في الوضعين: الفصل بين كل جارَين فوق
 * العتبة، والتباين عن السطح فوق ٣:١. وفصلُ التريتان في نطاق الأرضية،
 * فيلزمه ترميزٌ ثانٍ — ولذلك يحمل كل صفٍّ اسمَه وعددَه مكتوبَين، ولا
 * يُترك اللون وحده يقول مَن هو.
 */
const PROVIDER_TONES: Record<string, string> = {
  ZOOM: 'var(--mv-1)',
  ONSITE: 'var(--mv-2)',
  TEAMS: 'var(--mv-3)',
  MEET: 'var(--mv-4)',
  JITSI: 'var(--brand)',
};


/*
 * أسماءٌ قصيرةٌ مميَّزة لا اقتطاعٌ أعمى.
 *
 * قصُّ ثلاثة أحرفٍ من الاسم الكامل يُخرج «الا» للاثنين و«الا» للأربعاء
 * معًا — عمودان لا يُفرَّق بينهما. فتُكتب مختصراتٌ تفترق أولَ حرفٍ لها.
 */
const DAY_SHORT = ['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت'];

/**
 * بداية الأسبوع المعروض — سبتًا، بإزاحةٍ بالأسابيع.
 *
 * أسبوع العمل هنا يبدأ السبت، فتثبيتُ العمود الأول عليه يجعل الشريط
 * يُقرأ كتقويم مكتبٍ لا كسبعة أيامٍ متحرّكة تبدأ من اليوم.
 */
function startOfWeek(offsetWeeks: number): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  /* getDay: الأحد صفر — والسبت ستّة، فالرجوع إليه بمقدار (اليوم + 1) % 7. */
  d.setDate(d.getDate() - ((d.getDay() + 1) % 7) + offsetWeeks * 7);
  return d;
}
/** بداية اليوم — بها تُقاس المسافة بالأيام لا بالساعات. */
function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

const MONTHS_AR = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
];

/** بدايةُ الفترةِ بحسبِ الحبيبةِ والإزاحةِ (سالبٌ ماضٍ، موجبٌ مستقبل). */
function calStart(grain: Grain, offset: number): Date {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  if (grain === 'week') return startOfWeek(offset);
  if (grain === 'month') return new Date(now.getFullYear(), now.getMonth() + offset, 1);
  return new Date(now.getFullYear() + offset, 0, 1);
}
function calEnd(grain: Grain, start: Date): Date {
  const e = new Date(start);
  if (grain === 'week') e.setDate(e.getDate() + 7);
  else if (grain === 'month') e.setMonth(e.getMonth() + 1);
  else e.setFullYear(e.getFullYear() + 1);
  return e;
}
function calLabel(grain: Grain, start: Date): string {
  if (grain === 'year') return String(start.getFullYear());
  if (grain === 'month') return `${MONTHS_AR[start.getMonth()]} ${start.getFullYear()}`;
  const last = new Date(start);
  last.setDate(last.getDate() + 6);
  return `${start.getDate()}/${start.getMonth() + 1} — ${last.getDate()}/${last.getMonth() + 1}`;
}

interface CalBar {
  label: string;
  count: number;
  done: number;
  current: boolean;
}

/** أعمدةُ التقويم: أيامٌ للأسبوعِ والشهر، وأشهرٌ للسنة. */
function calBars(grain: Grain, start: Date, items: MeetingRow[]): CalBar[] {
  const todayTs = startOfDay(new Date()).getTime();
  const nowY = new Date().getFullYear();
  const nowM = new Date().getMonth();

  if (grain === 'year') {
    const bars: CalBar[] = MONTHS_AR.map((m, i) => ({
      label: m.slice(0, 3),
      count: 0,
      done: 0,
      current: start.getFullYear() === nowY && i === nowM,
    }));
    for (const it of items) {
      if (!it.dueAt) continue;
      const dt = new Date(it.dueAt);
      if (dt.getFullYear() !== start.getFullYear()) continue;
      const b = bars[dt.getMonth()]!;
      b.count += 1;
      if (it.completedAt) b.done += 1;
    }
    return bars;
  }

  const days =
    grain === 'week' ? 7 : new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();
  const bars: CalBar[] = Array.from({ length: days }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return {
      label: grain === 'week' ? DAY_SHORT[d.getDay()]! : String(d.getDate()),
      count: 0,
      done: 0,
      current: startOfDay(d).getTime() === todayTs,
    };
  });
  for (const it of items) {
    if (!it.dueAt) continue;
    const idx = Math.round((startOfDay(new Date(it.dueAt)).getTime() - start.getTime()) / 86_400_000);
    if (idx >= 0 && idx < days) {
      bars[idx]!.count += 1;
      if (it.completedAt) bars[idx]!.done += 1;
    }
  }
  return bars;
}

/** هل يقعُ التاريخُ في الفترةِ الجاريةِ لهذه الحبيبة؟ (للفلترةِ في القوائم) */
function inGrainPeriod(iso: string | null | undefined, grain: Grain | null): boolean {
  if (grain === null) return true;
  if (!iso) return false;
  const start = calStart(grain, 0);
  const end = calEnd(grain, start);
  const t = new Date(iso).getTime();
  return t >= start.getTime() && t < end.getTime();
}

function daysFromToday(iso: string): number {
  const day = startOfDay(new Date(iso)).getTime();
  const today = startOfDay(new Date()).getTime();
  return Math.round((day - today) / 86_400_000);
}

/** المجموعة التي يقع فيها الموعد — بها تُقسَّم القائمة. */
function bucketOf(m: MeetingRow, now: number): string {
  if (!m.dueAt) return 'بلا موعد';
  if (new Date(m.dueAt).getTime() < now) return 'فات موعده';
  const d = daysFromToday(m.dueAt);
  if (d <= 0) return 'اليوم';
  if (d === 1) return 'غدًا';
  if (d <= 7) return 'هذا الأسبوع';
  return 'لاحقًا';
}

const BUCKET_ORDER = ['فات موعده', 'اليوم', 'غدًا', 'هذا الأسبوع', 'لاحقًا', 'بلا موعد'];

/**
 * أقسامُ الشاشةِ نمطًا قابلًا للترتيب — كملفِّ العميل.
 *
 * المؤشّراتُ عمودٌ يمتدّ عرضَ الشاشة، ثمّ عمودان: التقويمُ والقادمةُ في
 * الرئيس، وتوزيعُ الوسائلِ وما انعقد في الجانبي. ويسحبُ المستخدمُ أيَّها
 * إلى موضعه أو يُخفيه، ويُحفظُ اختيارُه.
 */
const MEET_SECTIONS: SectionDef[] = [
  { key: 'stats', title: 'المؤشّرات', column: 'full' },
  { key: 'calendar', title: 'تقويم الاجتماعات', column: 'main' },
  { key: 'upcoming', title: 'القادمة', column: 'main' },
  { key: 'providers', title: 'كيف نجتمع', column: 'side' },
  { key: 'past', title: 'ما انعقد', column: 'side' },
];

export function MeetingsList() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const [watchRec, setWatchRec] = useState<string | null>(null);
  /* الإنشاء من هنا: العميل يُنتقى والبيانات تُملأ في مكانها. */
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [pastSearch, setPastSearch] = useState('');
  /*
   * الأسبوع المعروض في الشريط — إزاحةٌ بالأسابيع عن أسبوع اليوم.
   *
   * الشريط كان يعرض سبعة أيامٍ من اليوم فحسب: لا يُرى ما قبله ولا ما
   * بعده، ولا يُعرف أيُّ تاريخٍ هو أيّ عمود. صار تقويمًا يتنقّل، وكلُّ
   * عمودٍ يحمل تاريخه — والأسبوع يبدأ سبتًا كتقويم المكاتب هنا.
   */
  /* تقويمُ الاجتماعات: حبيبةٌ (أسبوع/شهر/سنة) وإزاحةٌ تمشي في الاتجاهين. */
  const [calGrain, setCalGrain] = useState<Grain>('week');
  const [calOffset, setCalOffset] = useState(0);
  /* فلترُ القائمتين بالفترةِ الجارية — «الكل» افتراضًا. */
  const [upFilter, setUpFilter] = useState<Grain | null>(null);
  const [pastFilter, setPastFilter] = useState<Grain | null>(null);
  /* نمطُ الشاشة: ترتيبُ الأقسامِ وإخفاؤها وتكبيرُ واحدها — تفضيلٌ يبقى. */
  const layout = useProfileLayout('goyone.layout.meetings', MEET_SECTIONS);

  const upcoming = useQuery({
    queryKey: ['crm', 'meetings', 'upcoming'],
    queryFn: () =>
      api<{ items: MeetingRow[] }>(
        `/crm/activities${qs({ type: 'MEETING', status: 'PLANNED', sort: 'dueAt', order: 'asc', pageSize: 50 })}`,
      ),
  });

  const past = useQuery({
    queryKey: ['crm', 'meetings', 'past'],
    queryFn: () =>
      api<{ items: MeetingRow[] }>(
        `/crm/activities${qs({ type: 'MEETING', status: 'DONE', sort: 'completedAt', order: 'desc', pageSize: 20 })}`,
      ),
  });

  /*
   * أسبوع الشريط يُجلب بمداه لا يُشتقّ من «القادمة».
   *
   * القائمة القادمة مجدولةٌ ومستقبليّة، فاشتقاقُ الشريط منها يجعل
   * الأسابيع الماضية فارغةً أبدًا وإن انعقدت فيها اجتماعات. والمدى
   * الصريح يُحضر ما وقع وما سيقع معًا — والعمودُ عندئذٍ صادقٌ في
   * الاتجاهين.
   */
  const calS = useMemo(() => calStart(calGrain, calOffset), [calGrain, calOffset]);
  const calE = useMemo(() => calEnd(calGrain, calS), [calGrain, calS]);

  const calQuery = useQuery({
    queryKey: ['crm', 'meetings', 'cal', calGrain, calS.toISOString()],
    queryFn: () =>
      api<{ items: MeetingRow[] }>(
        `/crm/activities${qs({
          type: 'MEETING',
          dueFrom: calS.toISOString(),
          dueTo: calE.toISOString(),
          pageSize: 100,
          sort: 'dueAt',
          order: 'asc',
        })}`,
      ),
  });

  const complete = useMutation({
    mutationFn: (id: string) => api(`/crm/activities/${id}/complete`, { method: 'POST', body: {} }),
    onSuccess: () => {
      toast('أُنجز الاجتماع — ملخّصه في ملف صاحبه');
      void queryClient.invalidateQueries({ queryKey: ['crm', 'meetings'] });
      void queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });

  const now = Date.now();
  const all = upcoming.data?.items ?? [];

  const isOnline = (m: MeetingRow) =>
    Boolean(m.meetingProvider && m.meetingProvider !== 'ONSITE' && m.meetingProvider !== 'PHONE');

  /* الحال في أربعة أرقام — والفائتُ أولًا لأنه ما يستدعي فعلًا الآن. */
  const overdueCount = all.filter((m) => m.dueAt && new Date(m.dueAt).getTime() < now).length;
  const todayCount = all.filter(
    (m) => m.dueAt && new Date(m.dueAt).getTime() >= now && daysFromToday(m.dueAt) <= 0,
  ).length;
  const onlineCount = all.filter(isOnline).length;
  const onsiteCount = all.filter((m) => m.meetingProvider === 'ONSITE').length;

  /* أعمدةُ التقويمِ بحسبِ الحبيبة: أيامٌ للأسبوعِ والشهر، وأشهرٌ للسنة. */
  const bars = useMemo(
    () => calBars(calGrain, calS, calQuery.data?.items ?? []),
    [calGrain, calS, calQuery.data],
  );
  const barPeak = Math.max(1, ...bars.map((b) => b.count));
  const calLbl = calLabel(calGrain, calS);

  /* «كيف نجتمع» — توزيع الوسائل، كلٌّ باسمه وعدده مكتوبَين لا بلونه وحده. */
  const providers = useMemo(() => {
    const tally = new Map<string, number>();
    for (const m of all) {
      const key = m.meetingProvider ?? 'ONSITE';
      tally.set(key, (tally.get(key) ?? 0) + 1);
    }
    return [...tally.entries()]
      .map(([key, count]) => ({
        key,
        count,
        label: MEETING_PROVIDER_LABELS[key as keyof typeof MEETING_PROVIDER_LABELS] ?? key,
        tone: PROVIDER_TONES[key] ?? 'var(--ink-4)',
      }))
      .sort((a, b) => b.count - a.count);
  }, [all]);
  const providerPeak = Math.max(1, ...providers.map((p) => p.count));

  /* البحث في الشاشة نفسها: عنوانًا أو صاحبَ الاجتماع. */
  const term = search.trim().toLowerCase();
  const shown = all.filter(
    (m) =>
      inGrainPeriod(m.dueAt, upFilter) &&
      (!term ||
        [m.subject, m.lead?.name, m.account?.name, m.contact?.name]
          .filter(Boolean)
          .some((s) => String(s).toLowerCase().includes(term))),
  );

  /* المجموعات بترتيبها الزمنيّ — والفارغة لا تُعرض. */
  const grouped = BUCKET_ORDER.map((name) => ({
    name,
    items: shown.filter((m) => bucketOf(m, now) === name),
  })).filter((g) => g.items.length > 0);

  /* المنقضيةُ بالبحثِ نفسِه — لتتماثلَ البطاقتان. */
  const pastTerm = pastSearch.trim().toLowerCase();
  const pastShown = (past.data?.items ?? []).filter(
    (m) =>
      inGrainPeriod(m.completedAt, pastFilter) &&
      (!pastTerm ||
        [m.subject, m.lead?.name, m.account?.name, m.contact?.name]
          .filter(Boolean)
          .some((s) => String(s).toLowerCase().includes(pastTerm))),
  );

  return (
    <div className="page">
      <PageHead
        title="الاجتماعات"
        subtitle="القادمة برابط دخولها، والمنقضية بملخّصها — من كل ملفات العملاء"
        actions={
          <>
            {/* تغييرُ النمط: يسحبُ المستخدمُ الأقسامَ ويُخفي ما لا يحتاج. */}
            <ProfileLayoutButton api={layout} />
            {/*
              رابط الحجز: بدل التراسل على موعدٍ يناسب الطرفين، يرى
              العميلُ الشاغرَ بنفسه فيختار — والموعد يقع هنا.
            */}
            <BookingLinkButton />
            {/*
              والإنشاء في مكانه لا إحالةً إلى الأنشطة: تُنتقى جهةُ
              الاجتماع ثم يُملأ نموذجُه — النموذجُ نفسه الذي في الملف.
            */}
            <button className="btn btn-sm btn-primary" onClick={() => setCreating(true)}>
              <Icon name="plus" size={14} />
              اجتماع جديد
            </button>
          </>
        }
      />

      <ProfileLayoutProvider api={layout}>
        <ProfileLayoutBar api={layout} />

        {/* عمودٌ بعرض الشاشة أوّلًا — المؤشّرات — ثمّ العمودان تحته. */}
        <LayoutColumn api={layout} column="full">
          <Section layoutKey="stats">
      {/* ---------------- الحال في أربعة أرقام ---------------- */}
      <div className="meet-stats">
        {(
          [
            ['فات موعده', overdueCount, 'alert', 'var(--danger)', overdueCount > 0],
            ['اليوم', todayCount, 'clock', 'var(--warning)', false],
            ['حضوري', onsiteCount, 'building', 'var(--accent-600)', false],
            ['عن بُعد', onlineCount, 'video', 'var(--brand)', false],
            ['كل القادمة', all.length, 'calendar', 'var(--ink-3)', false],
          ] as Array<[string, number, Parameters<typeof Icon>[0]['name'], string, boolean]>
        ).map(([label, value, icon, tone, warn]) => (
          <div key={label} className={`meet-stat${warn ? ' is-warn' : ''}`}>
            <span className="meet-stat-ico" style={{ color: tone }}>
              <Icon name={icon} size={16} />
            </span>
            <span className="meet-stat-body">
              <strong className="num">{value}</strong>
              <span className="tiny mute">{label}</span>
            </span>
          </div>
        ))}
      </div>
          </Section>
        </LayoutColumn>

        {/* العمودان: التقويمُ والقادمةُ في الرئيس، التوزيعُ وما انعقد في الجانبي. */}
        <div className="two-col pl-two">
          <LayoutColumn api={layout} column="main">
            {/* ---------------- التحليل: متى نجتمع ---------------- */}
            <Section layoutKey="calendar">
        <div className="card">
          <div className="card-head mv-anal-head is-cal">
            <h2 className="card-title">
              <span className="mv-anal-ic">
                <Icon name="calendar" size={15} />
              </span>
              تقويم الاجتماعات
            </h2>
            {/* حبيبةٌ (أسبوع/شهر/سنة) وسهمان يمشيان في الاتجاهين، و«الآن» يعود —
             * كلُّها على الجنبِ في سطرٍ واحدٍ لا تنكسر. */}
            <span className="mv-cal-nav">
              <GrainSwitch
                value={calGrain}
                onChange={(g) => {
                  if (g) setCalGrain(g);
                  setCalOffset(0);
                }}
              />
              <span className="row" style={{ gap: 4 }}>
                <button
                  className="btn btn-sm btn-icon"
                  onClick={() => setCalOffset((o) => o - 1)}
                  title="السابق"
                  aria-label="الفترة السابقة"
                >
                  <Icon name="chevronRight" size={14} />
                </button>
                <span className="mv-range num">{calLbl}</span>
                <button
                  className="btn btn-sm btn-icon"
                  onClick={() => setCalOffset((o) => o + 1)}
                  title="التالي"
                  aria-label="الفترة التالية"
                >
                  <Icon name="chevronLeft" size={14} />
                </button>
                {calOffset !== 0 && (
                  <button className="btn btn-sm" onClick={() => setCalOffset(0)}>
                    الآن
                  </button>
                )}
              </span>
            </span>
          </div>
          <div className="card-body">
            {calQuery.isPending ? (
              <div className="skeleton" style={{ height: 132 }} />
            ) : (
              <div
                className={`mv-week g-${calGrain}`}
                role="img"
                aria-label={`عدد الاجتماعات في ${calLbl}`}
              >
                {bars.map((b, i) => (
                  <div
                    className="mv-day"
                    key={i}
                    title={`${b.label} — ${b.count} اجتماع${b.done ? ` (${b.done} منجَز)` : ''}`}
                  >
                    <span className={`mv-num num${b.count ? '' : ' is-zero'}`}>{b.count}</span>
                    <span className="mv-track">
                      <span
                        className={`mv-fill${b.current ? ' is-today' : ''}`}
                        style={{ height: `${Math.round((b.count / barPeak) * 100)}%` }}
                      />
                    </span>
                    <span className={`mv-dayname${b.current ? ' is-today' : ''}`}>{b.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
            </Section>

            {/* ---------------- القادمة ---------------- */}
            <Section layoutKey="upcoming">
        <div className="card">
          <div className="card-head mv-head is-up">
            <span className="mv-head-title">
              <span className="mv-head-ic">
                <Icon name="calendar" size={16} />
              </span>
              <h2 className="card-title">القادمة</h2>
              <GrainSwitch value={upFilter} withAll onChange={setUpFilter} />
            </span>
            <SearchBox
              value={search}
              onChange={setSearch}
              placeholder="ابحث بعنوان الاجتماع أو صاحبه"
            />
          </div>
          <div className="card-body">
            {upcoming.isPending && <div className="skeleton" style={{ height: 160 }} />}
            {/* الخطأ يُقال ويُعاد منه — لا يُترك فراغًا يُقرأ «لا شيء». */}
            {upcoming.isError && (
              <ErrorState error={upcoming.error} onRetry={() => void upcoming.refetch()} />
            )}
            {upcoming.data && shown.length === 0 && (
              <EmptyState
                title={term ? 'لا اجتماع يطابق البحث' : 'لا اجتماعات قادمة'}
                text={
                  term
                    ? 'جرّب كلمةً أخرى — أو امسح البحث.'
                    : 'سجّل اجتماعًا من ملف عميلٍ أو من الأنشطة — يظهر هنا بموعده ورابط دخوله.'
                }
              />
            )}

            {/* مجمَّعةً بأيامها: «فات موعده» ثم «اليوم» — لا سيلٌ من خمسين سطرًا.
             * والقائمةُ تُمرَّر داخليًّا فلا تطول الصفحةُ كلُّها بامتدادها. */}
            <div className="mv-scroll">
            {grouped.map((group) => (
              <div key={group.name} className="col mv-group-sec" style={{ gap: 'var(--sp-2)' }}>
                {group.items.map((m) => {
                    const overdue = Boolean(m.dueAt && new Date(m.dueAt).getTime() < now);
                    const online = isOnline(m);
                    const who = m.lead ?? m.contact ?? m.account;
                    const when = m.dueAt ? new Date(m.dueAt) : null;
                    const maps = m.mapsUrl ?? m.lead?.mapsUrl ?? null;
                    return (
                      <article
                        key={m.id}
                        className={`mv-card${overdue ? ' is-late' : ''}`}
                        style={{
                          ['--mv-tone' as string]:
                            PROVIDER_TONES[m.meetingProvider ?? 'ONSITE'] ?? 'var(--ink-4)',
                        }}
                      >
                        {/* كتلة الوقت: يومٌ ورقمٌ وساعة — تُقرأ قبل العنوان. */}
                        <span className="mv-when" aria-hidden>
                          <span className="mv-when-day">
                            {when ? DAY_SHORT[when.getDay()] : '—'}
                          </span>
                          <strong className="mv-when-num num">{when ? when.getDate() : '—'}</strong>
                          <span className="mv-when-time num">
                            {when
                              ? when.toLocaleTimeString('ar-EG', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })
                              : ''}
                          </span>
                        </span>

                        <div className="mv-body">
                          <div className="row" style={{ gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
                            <button
                              className="link-btn cell-strong"
                              onClick={() => setOpenId(m.id)}
                              title="افتح تفاصيل الاجتماع"
                            >
                              {m.subject}
                            </button>
                            <Badge kind={online ? 'info' : 'neutral'}>
                              {MEETING_PROVIDER_LABELS[
                                (m.meetingProvider ??
                                  'ONSITE') as keyof typeof MEETING_PROVIDER_LABELS
                              ] ?? m.meetingProvider}
                            </Badge>
                            {who && (
                              <Link
                                to={
                                  m.lead
                                    ? `/crm/leads/${m.lead.id}`
                                    : `/crm/accounts/${m.account?.id ?? ''}`
                                }
                                className="link small"
                              >
                                {who.name}
                              </Link>
                            )}
                            <span style={{ marginInlineStart: 'auto' }}>
                              <OwnerChip ownerId={m.assigneeId} size={20} showName={false} />
                            </span>
                          </div>

                          <div
                            className="row mv-meta"
                            style={{ gap: 'var(--sp-3)', flexWrap: 'wrap' }}
                          >
                            {m.dueAt && (
                              <span className={`tiny${overdue ? ' mv-late-text' : ' mute'}`}>
                                <Icon name="clock" size={12} /> {dateTime(m.dueAt)}
                                {m.durationMin ? ` — ${m.durationMin} دقيقة` : ''}
                                {overdue ? ' · فات موعده' : ''}
                              </span>
                            )}
                            {!online && m.location && (
                              <span className="tiny mute">
                                <Icon name="building" size={12} /> {m.location}
                              </span>
                            )}

                            <span className="row mv-actions" style={{ gap: 6 }}>
                              {/* Jitsi الداخليّ يُفتح داخلَ النظام؛ غيرُه رابطٌ خارجيّ. */}
                              {m.meetingProvider === 'JITSI' ? (
                                <Link
                                  className="btn btn-sm act-open is-meeting"
                                  to={`/crm/meetings/${m.id}/room`}
                                >
                                  <Icon name="video" size={13} />
                                  ادخل الاجتماع
                                </Link>
                              ) : (
                                online &&
                                m.meetingUrl && (
                                  <a
                                    className="btn btn-sm act-open is-meeting"
                                    href={m.meetingUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <Icon name="video" size={13} />
                                    ادخل الاجتماع
                                  </a>
                                )
                              )}
                              {/* الحضوريّ بموقعه — يفتح الخريطة ويذهب. */}
                              {!online && maps && (
                                <a
                                  className="btn btn-sm act-nav"
                                  href={maps}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <Icon name="mapPin" size={13} />
                                  الملاحة
                                </a>
                              )}
                              <button
                                className="btn btn-sm"
                                disabled={complete.isPending}
                                onClick={() => complete.mutate(m.id)}
                                title="تعليم كمنجَز"
                              >
                                <Icon name="check" size={13} />
                                تمّ
                              </button>
                            </span>
                          </div>
                        </div>
                      </article>
                    );
                  })}
              </div>
            ))}
            </div>
          </div>
        </div>
            </Section>
          </LayoutColumn>

          <LayoutColumn api={layout} column="side">
            {/* ---------------- كيف نجتمع: توزيع الوسائل ---------------- */}
            <Section layoutKey="providers">
        <div className="card">
          <div className="card-head mv-anal-head is-dist">
            <h2 className="card-title">
              <span className="mv-anal-ic">
                <Icon name="chart" size={15} />
              </span>
              كيف نجتمع
            </h2>
            <span className="tiny mute">توزيع الوسائل</span>
          </div>
          <div className="card-body">
            {providers.length === 0 ? (
              <p className="mute small">لا اجتماعات قادمة.</p>
            ) : (
              <div className="mv-providers">
                {providers.map((p) => (
                  <div className="mv-prov" key={p.key}>
                    <span className="mv-prov-label">
                      <span className="mv-dot" style={{ background: p.tone }} aria-hidden />
                      {p.label}
                    </span>
                    <span className="mv-prov-track">
                      <span
                        className="mv-prov-fill"
                        style={{
                          width: `${Math.round((p.count / providerPeak) * 100)}%`,
                          background: p.tone,
                        }}
                      />
                    </span>
                    <strong className="num mv-prov-num">{p.count}</strong>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
            </Section>

            {/* ---------------- المنقضية ---------------- */}
            <Section layoutKey="past">
        <div className="card">
          <div className="card-head mv-head is-done">
            <span className="mv-head-title">
              <span className="mv-head-ic">
                <Icon name="check" size={16} />
              </span>
              <h2 className="card-title">ما انعقد</h2>
              <GrainSwitch value={pastFilter} withAll onChange={setPastFilter} />
            </span>
            <SearchBox
              value={pastSearch}
              onChange={setPastSearch}
              placeholder="ابحث بعنوان الاجتماع أو صاحبه"
            />
          </div>
          <div className="card-body">
            {/* المنقضية تستحق ما تستحقّه القادمة: هيكلًا وخطأً مُعلَنًا وفراغًا موحَّدًا. */}
            {past.isPending && <div className="skeleton" style={{ height: 120 }} />}
            {past.isError && <ErrorState error={past.error} onRetry={() => void past.refetch()} />}
            {past.data && pastShown.length === 0 && (
              <EmptyState
                title={pastTerm ? 'لا اجتماع يطابق البحث' : 'لا اجتماعات منقضية بعد'}
                text={
                  pastTerm ? 'جرّب كلمةً أخرى — أو امسح البحث.' : 'ما يُعلَّم «تمّ» يظهر هنا بملخّصه.'
                }
              />
            )}

            {/* المنقضيةُ بنفسِ بطاقةِ القادمةِ لكن بلمسةٍ خضراءَ (تمّت) لا حمراء. */}
            <div className="col mv-scroll" style={{ gap: 'var(--sp-2)' }}>
              {pastShown.map((m) => {
                const when = m.completedAt ? new Date(m.completedAt) : null;
                const online = isOnline(m);
                const who = m.lead ?? m.contact ?? m.account;
                return (
                  <article
                    key={m.id}
                    className="mv-card mv-done"
                    style={{ ['--mv-tone' as string]: 'var(--accent-600)' }}
                  >
                    <span className="mv-when" aria-hidden>
                      <span className="mv-when-day">{when ? DAY_SHORT[when.getDay()] : '—'}</span>
                      <strong className="mv-when-num num">{when ? when.getDate() : '—'}</strong>
                      <span className="mv-when-time num">
                        {when
                          ? when.toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })
                          : ''}
                      </span>
                    </span>

                    <div className="mv-body">
                      <div className="row" style={{ gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
                        <button
                          className="link-btn cell-strong"
                          onClick={() => setOpenId(m.id)}
                          title="افتح تفاصيل الاجتماع"
                        >
                          {m.subject}
                        </button>
                        <Badge kind={online ? 'info' : 'neutral'}>
                          {MEETING_PROVIDER_LABELS[
                            (m.meetingProvider ?? 'ONSITE') as keyof typeof MEETING_PROVIDER_LABELS
                          ] ?? m.meetingProvider}
                        </Badge>
                        {m.recordingUrl && <Badge kind="success">مسجَّل</Badge>}
                        {who && (
                          <Link
                            to={
                              m.lead
                                ? `/crm/leads/${m.lead.id}`
                                : `/crm/accounts/${m.account?.id ?? ''}`
                            }
                            className="link small"
                          >
                            {who.name}
                          </Link>
                        )}
                        <span style={{ marginInlineStart: 'auto' }}>
                          <OwnerChip ownerId={m.assigneeId} size={20} showName={false} />
                        </span>
                      </div>

                      <div className="row mv-meta" style={{ gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
                        {m.completedAt && (
                          <span className="tiny mv-done-text">
                            <Icon name="check" size={12} /> {dateTime(m.completedAt)} ·{' '}
                            {relative(m.completedAt)}
                          </span>
                        )}
                        <span className="row mv-actions" style={{ gap: 6 }}>
                          {m.recordingUrl && (
                            <button
                              className="btn btn-sm act-open is-meeting"
                              onClick={() => setWatchRec(m.recordingUrl ?? null)}
                            >
                              <Icon name="play" size={13} /> شاهد التسجيل
                            </button>
                          )}
                          <button className="btn btn-sm" onClick={() => setOpenId(m.id)}>
                            <Icon name="expand" size={13} /> التفاصيل
                          </button>
                        </span>
                      </div>
                      {m.body && <p className="small mv-past-body">{m.body}</p>}
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
            </Section>
          </LayoutColumn>
        </div>
      </ProfileLayoutProvider>

      {creating && (
        <NewActivityModal
          title="اجتماع جديد"
          defaultType="MEETING"
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['crm', 'meetings'] });
            void queryClient.invalidateQueries({ queryKey: ['activities'] });
          }}
        />
      )}

      {openId && <ActivityDetail id={openId} onClose={() => setOpenId(null)} />}
      {watchRec && (
        <RecordingPlayerModal url={watchRec} video onClose={() => setWatchRec(null)} />
      )}
    </div>
  );
}
